import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import ActionButtonOverlay from '../../components/navigation/ActionButtonOverlay';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import Icon from '../../components/AppIcon';
import symptomService from '../../services/symptomService';
import ConsultationHeader from './components/ConsultationHeader';
import PrimarySymptomCard from './components/PrimarySymptomCard';
import DiscussionQuestions from './components/DiscussionQuestions';
import ConsultationActions from './components/ConsultationActions';
import { trackDoctorExport } from '../../hooks/useGoogleAnalytics';
import { trackDoctorReportGenerated } from '../../hooks/useGoogleAnalytics';

const PrepareForDoctorVisit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();

  const [reportPeriod, setReportPeriod] = useState(30);
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(
    location?.state?.summaryData || {
      symptoms: [], trends: [], triggers: [], impacts: [], medications: [], notes: [], rawEntries: []
    }
  );
  const [lastNote, setLastNote] = useState('');

  const patientName = userProfile?.first_name
    ? `${userProfile?.first_name} ${userProfile?.last_name || ''}`?.trim()
    : user?.email || 'Patient';

  const formatLabel = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());

  const getSeverityColor = (severity) => {
    if (severity >= 8) return 'text-red-600';
    if (severity >= 5) return 'text-amber-600';
    return 'text-green-600';
  };

  const getSeverityBadge = (severity) => {
    if (severity >= 8) return 'bg-red-50 text-red-700 border border-red-200';
    if (severity >= 5) return 'bg-amber-50 text-amber-700 border border-amber-200';
    return 'bg-green-50 text-green-700 border border-green-200';
  };

  const getTrendStyle = (direction) => {
    if (direction === 'increasing') return 'text-red-600 bg-red-50 border border-red-200';
    if (direction === 'decreasing') return 'text-green-600 bg-green-50 border border-green-200';
    return 'text-amber-600 bg-amber-50 border border-amber-200';
  };

  const getTrendIcon = (direction) => {
    if (direction === 'increasing') return '↑';
    if (direction === 'decreasing') return '↓';
    return '→';
  };

  useEffect(() => {
    if (user) {
      fetchDataForPeriod(reportPeriod);
    }
  }, [reportPeriod, user]);

  const fetchDataForPeriod = async (days) => {
    setLoading(true);
    const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const end = new Date();

    const { data, error } = await symptomService?.getEntries({
      dateRange: { start: start?.toISOString(), end: end?.toISOString() }
    });

    if (!error && data) {
      processData(data);
    }
    setLoading(false);
  };

  const processData = (entries) => {
    if (!entries?.length) {
      setSummaryData({ symptoms: [], trends: [], triggers: [], impacts: [], medications: [], notes: [], rawEntries: [] });
      return;
    }

    const symptomMap = new Map();
    entries?.forEach(entry => {
      const categories = entry?.symptom_categories || [];
      const locations = entry?.body_locations || [];
      const severity = entry?.severity_numeric || 0;
      categories?.forEach((category, index) => {
        const location = locations?.[index] || 'Not specified';
        const key = `${category}-${location}`;
        if (!symptomMap?.has(key)) {
          symptomMap?.set(key, { name: category, location, severities: [], frequency: 0 });
        }
        const s = symptomMap?.get(key);
        s?.severities?.push(severity);
        s.frequency += 1;
      });
    });

    const symptoms = Array?.from(symptomMap?.values())?.map(s => ({
      name: s?.name,
      location: s?.location,
      avgSeverity: s?.severities?.length > 0
        ? Math?.round((s?.severities?.reduce((a, b) => a + b, 0) / s?.severities?.length) * 10) / 10
        : 0,
      frequency: s?.frequency
    }));

    const trends = symptoms?.map(symptom => {
      const midpoint = Math?.floor(entries?.length / 2);
      const firstHalf = entries?.slice(0, midpoint);
      const secondHalf = entries?.slice(midpoint);
      const firstAvg = firstHalf?.filter(e => e?.symptom_categories?.includes(symptom?.name))
        ?.reduce((sum, e) => sum + (e?.severity_numeric || 0), 0) / (firstHalf?.length || 1);
      const secondAvg = secondHalf?.filter(e => e?.symptom_categories?.includes(symptom?.name))
        ?.reduce((sum, e) => sum + (e?.severity_numeric || 0), 0) / (secondHalf?.length || 1);
      const change = secondAvg - firstAvg;
      let direction = 'stable';
      if (change > 0.5) direction = 'increasing';
      else if (change < -0.5) direction = 'decreasing';
      return { symptom: symptom?.name, direction, change: direction?.charAt(0)?.toUpperCase() + direction?.slice(1) };
    });

    const triggerSet = new Set();
    entries?.forEach(entry => {
      (entry?.triggers || [])?.forEach(t => triggerSet?.add(t));
      if (entry?.custom_trigger) triggerSet?.add(entry?.custom_trigger);
    });

    const impactMap = new Map();
    entries?.forEach(entry => {
      (entry?.daily_impacts || [])?.forEach(impact => {
        if (!impactMap?.has(impact)) impactMap?.set(impact, []);
        impactMap?.get(impact)?.push(entry?.severity_numeric || 5);
      });
    });
    const impacts = Array?.from(impactMap?.entries())?.map(([category, severities]) => ({
      category,
      score: Math?.round((severities?.reduce((a, b) => a + b, 0) / severities?.length) * 10) / 10
    }));

    const medicationMap = new Map();
    entries?.forEach(entry => {
      const status = entry?.medication_status || 'Not specified';
      medicationMap?.set(status, (medicationMap?.get(status) || 0) + 1);
    });
    const medications = Array?.from(medicationMap?.entries())?.map(([status, count]) => ({ status, count }));

    const rawEntries = entries?.map(entry => ({
      id: entry?.id,
      date: entry?.created_at,
      feelingLevel: entry?.feeling_level,
      symptomCategories: entry?.symptom_categories || [],
      bodyLocations: entry?.body_locations || [],
      severityNumeric: entry?.severity_numeric,
      triggers: entry?.triggers || [],
      customTrigger: entry?.custom_trigger || '',
      dailyImpacts: entry?.daily_impacts || [],
      medicationStatus: entry?.medication_status || '',
      medicationDetails: entry?.medication_details || '',
      notes: entry?.notes || ''
    }));

    const newSummaryData = { symptoms, trends, triggers: Array?.from(triggerSet), impacts, medications, notes: [], rawEntries };
    setSummaryData(newSummaryData);

    // Save real data to sessionStorage for doctor-report-view
    try {
      const fmt = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
      const sortedSymptoms = [...symptoms]?.sort((a, b) => {
        const scoreA = (a?.avgSeverity || 0) * 0.6 + (a?.frequency || 0) * 0.4;
        const scoreB = (b?.avgSeverity || 0) * 0.6 + (b?.frequency || 0) * 0.4;
        return scoreB - scoreA;
      });
      const primary = sortedSymptoms?.[0] || null;
      const primaryTrendData = primary ? trends?.find(t => t?.symptom === primary?.name) : null;
      const periodStart = new Date(Date.now() - reportPeriod * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const periodEnd = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const allNoteEntries = rawEntries?.filter(e => e?.notes);
      const reportData = {
        patientName: userProfile?.first_name
          ? `${userProfile?.first_name} ${userProfile?.last_name || ''}`?.trim()
          : user?.email || 'Patient',
        reportPeriod: `${periodStart} \u2013 ${periodEnd}`,
        generatedAt: new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' + new Date()?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        totalEntries: rawEntries?.length,
        primarySymptom: primary ? {
          name: fmt(primary?.name),
          location: fmt(primary?.location || 'Not specified'),
          avgSeverity: primary?.avgSeverity,
          trend: primaryTrendData?.direction || 'stable',
          frequency: `${primary?.frequency} occurrence(s) in ${reportPeriod} days`,
          impactLevel: primary?.avgSeverity >= 8 ? 'High' : primary?.avgSeverity >= 5 ? 'Moderate' : 'Low',
        } : null,
        triggers: Array?.from(triggerSet),
        impacts: impacts?.map(imp => ({ area: fmt(imp?.category), score: imp?.score })),
        timeline: [...rawEntries]?.sort((a, b) => new Date(b?.date) - new Date(a?.date))?.slice(0, 8)?.map(entry => ({
          date: new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          symptoms: entry?.symptomCategories?.map(c => fmt(c))?.join(', ') || 'No symptoms',
          severity: entry?.severityNumeric || 0,
          note: entry?.notes || ''
        })),
        patientNotes: allNoteEntries?.slice(0, 3)?.map(e => e?.notes)?.join(' ') || 'No notes recorded.'
      };
      sessionStorage.setItem('doctorReportData', JSON.stringify(reportData));
    } catch (e) {
      // ignore storage errors
    }
  };

  const primaryConcern = summaryData?.symptoms?.length > 0
    ? [...summaryData?.symptoms]?.sort((a, b) => {
        const scoreA = (a?.avgSeverity || 0) * 0.6 + (a?.frequency || 0) * 0.4;
        const scoreB = (b?.avgSeverity || 0) * 0.6 + (b?.frequency || 0) * 0.4;
        return scoreB - scoreA;
      })?.[0]
    : null;

  const primaryTrend = primaryConcern
    ? summaryData?.trends?.find(t => t?.symptom === primaryConcern?.name)
    : null;

  const rawEntries = summaryData?.rawEntries || [];

  const timelineEntries = [...rawEntries]
    ?.sort((a, b) => new Date(b?.date) - new Date(a?.date))
    ?.slice(0, 5);

  const allNotes = rawEntries?.filter(e => e?.notes);
  const notesSummary = allNotes?.slice(0, 2)?.map(e => e?.notes)?.join(' ');

  const handleExportPDF = () => {
    window.print();
    trackDoctorExport('pdf_consultation');
    trackDoctorReportGenerated('consultation_snapshot');
  };

  const handleShare = () => {
    const formatLabel = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());

    const reportDate = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const periodStart = new Date(Date.now() - reportPeriod * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodEnd = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    const lines = [];

    lines?.push('═══════════════════════════════════════');
    lines?.push('   MYBODYTODAY — CONSULTATION SNAPSHOT');
    lines?.push('═══════════════════════════════════════');
    lines?.push('');

    // 1. Patient & Report Period
    lines?.push('PATIENT INFORMATION');
    lines?.push('───────────────────');
    lines?.push(`Patient Name : ${patientName}`);
    lines?.push(`Report Period: ${periodStart} – ${periodEnd} (Last ${reportPeriod} days)`);
    lines?.push(`Generated    : ${reportDate}`);
    lines?.push(`Total Entries: ${rawEntries?.length}`);
    lines?.push(`Symptoms Tracked: ${summaryData?.symptoms?.length}`);
    lines?.push('');

    // 2. Primary Concern
    lines?.push('PRIMARY CONCERN');
    lines?.push('───────────────');
    if (primaryConcern) {
      lines?.push(`Symptom  : ${formatLabel(primaryConcern?.name)}`);
      lines?.push(`Location : ${formatLabel(primaryConcern?.location || 'Not specified')}`);
      lines?.push(`Avg Severity: ${primaryConcern?.avgSeverity}/10`);
      lines?.push(`Frequency: ${primaryConcern?.frequency} occurrence(s)`);
      const trend = primaryTrend?.direction || 'stable';
      const trendIcon = trend === 'increasing' ? '↑ Worsening' : trend === 'decreasing' ? '↓ Improving' : '→ Stable';
      lines?.push(`Trend    : ${trendIcon}`);
    } else {
      lines?.push('No primary concern identified.');
    }
    lines?.push('');

    // 3. Symptom Summary
    if (summaryData?.symptoms?.length > 0) {
      lines?.push('SYMPTOM SUMMARY');
      lines?.push('───────────────');
      summaryData?.symptoms?.forEach((s, i) => {
        const trend = summaryData?.trends?.find(t => t?.symptom === s?.name);
        const trendIcon = trend?.direction === 'increasing' ? '↑' : trend?.direction === 'decreasing' ? '↓' : '→';
        lines?.push(`${i + 1}. ${formatLabel(s?.name)} (${formatLabel(s?.location)})`);
        lines?.push(`   Severity: ${s?.avgSeverity}/10 | Frequency: ${s?.frequency}× | Trend: ${trendIcon} ${trend?.change || 'Stable'}`);
      });
      lines?.push('');
    }

    // 4. Symptom Timeline
    const allTimeline = [...rawEntries]?.sort((a, b) => new Date(b?.date) - new Date(a?.date));
    if (allTimeline?.length > 0) {
      lines?.push('SYMPTOM TIMELINE');
      lines?.push('────────────────');
      allTimeline?.forEach(entry => {
        const dateStr = new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const symptoms = entry?.symptomCategories?.map(c => formatLabel(c))?.join(', ') || 'No symptoms';
        const severity = entry?.severityNumeric > 0 ? ` | Severity: ${entry?.severityNumeric}/10` : '';
        lines?.push(`${dateStr}: ${symptoms}${severity}`);
        if (entry?.notes) {
          lines?.push(`   Note: ${entry?.notes}`);
        }
      });
      lines?.push('');
    }

    // 5. Trigger Observations
    lines?.push('TRIGGER OBSERVATIONS');
    lines?.push('────────────────────');
    if (summaryData?.triggers?.length > 0) {
      summaryData?.triggers?.forEach((t, i) => lines?.push(`${i + 1}. ${t}`));
    } else {
      lines?.push('No specific triggers identified.');
    }
    lines?.push('');

    // 6. Impact on Daily Life
    lines?.push('IMPACT ON DAILY LIFE');
    lines?.push('────────────────────');
    const impactCategories = ['mobility', 'sleep', 'social', 'work'];
    if (summaryData?.impacts?.length > 0) {
      summaryData?.impacts?.forEach(impact => {
        lines?.push(`${formatLabel(impact?.category)}: ${impact?.score}/10`);
      });
    } else {
      lines?.push('No daily impact data recorded.');
    }
    lines?.push('');

    // 7. Medication Summary
    lines?.push('MEDICATION SUMMARY');
    lines?.push('──────────────────');
    if (summaryData?.medications?.length > 0) {
      summaryData?.medications?.forEach(med => {
        lines?.push(`${formatLabel(med?.status)}: ${med?.count} entry(ies)`);
      });
      // Include medication details from raw entries
      const medDetails = rawEntries?.filter(e => e?.medicationDetails)?.map(e => e?.medicationDetails);
      if (medDetails?.length > 0) {
        lines?.push('Details:');
        [...new Set(medDetails)]?.forEach(d => lines?.push(`  • ${d}`));
      }
    } else {
      lines?.push('No medications recorded.');
    }
    lines?.push('');

    // 8. Patient Notes Summary
    const allNoteEntries = rawEntries?.filter(e => e?.notes);
    if (allNoteEntries?.length > 0) {
      lines?.push('PATIENT NOTES SUMMARY');
      lines?.push('─────────────────────');
      const combinedNotes = allNoteEntries?.slice(0, 3)?.map(e => e?.notes)?.join(' ');
      lines?.push(combinedNotes);
      lines?.push('');
    }

    // 9. Detailed Notes with Dates
    if (allNoteEntries?.length > 0) {
      lines?.push('DETAILED PATIENT NOTES');
      lines?.push('──────────────────────');
      allNoteEntries?.forEach(entry => {
        const dateStr = new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        lines?.push(`[${dateStr}] ${entry?.notes}`);
      });
      lines?.push('');
    }

    // 10. Suggested Discussion Questions
    lines?.push('SUGGESTED DISCUSSION QUESTIONS');
    lines?.push('──────────────────────────────');
    const questions = [];
    if (primaryConcern) {
      questions?.push(`1. What could be causing my ${formatLabel(primaryConcern?.name)} in my ${formatLabel(primaryConcern?.location || 'body')}?`);
    }
    if (primaryTrend?.direction === 'increasing') {
      questions?.push(`2. My symptoms appear to be worsening — should I be concerned?`);
    } else {
      questions?.push(`2. Are my current symptoms at a level that requires further investigation?`);
    }
    if (summaryData?.triggers?.length > 0) {
      questions?.push(`3. Could ${summaryData?.triggers?.slice(0, 2)?.join(' or ')} be contributing to my symptoms?`);
    } else {
      questions?.push(`3. What tests or investigations would you recommend based on this history?`);
    }
    if (summaryData?.impacts?.length > 0) {
      questions?.push(`4. My symptoms are affecting my ${formatLabel(summaryData?.impacts?.[0]?.category || 'daily life')} — what can I do to manage this?`);
    } else {
      questions?.push(`4. What lifestyle changes might help reduce the frequency or severity of my symptoms?`);
    }
    questions?.forEach(q => lines?.push(q));
    lines?.push('');

    // Safety Disclaimer
    lines?.push('SAFETY DISCLAIMER');
    lines?.push('─────────────────');
    lines?.push('This report is a patient-generated symptom summary and is NOT a medical diagnosis.');
    lines?.push('All data is self-reported via the MyBodyToday app. Please consult a qualified');
    lines?.push('healthcare professional for diagnosis and treatment.');
    lines?.push('');
    lines?.push('Generated by MyBodyToday — mybodytoda1849.builtwithrocket.new');
    lines?.push('═══════════════════════════════════════');

    const text = lines?.join('\n');

    if (navigator?.share) {
      navigator?.share({ title: 'MyBodyToday Consultation Snapshot', text });
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  };

  const handleSave = () => {
    const report = {
      patientName,
      period: reportPeriod,
      generatedAt: new Date()?.toISOString(),
      primaryConcern,
      triggers: summaryData?.triggers,
      impacts: summaryData?.impacts,
      notesSummary
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consultation-snapshot-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const buildReportText = () => {
    const fmt = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
    const reportDate = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const periodStart = new Date(Date.now() - reportPeriod * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodEnd = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const lines = [];
    lines?.push('═══════════════════════════════════════');
    lines?.push('   MYBODYTODAY — CONSULTATION SNAPSHOT');
    lines?.push('═══════════════════════════════════════');
    lines?.push('');
    lines?.push('PATIENT INFORMATION');
    lines?.push('───────────────────');
    lines?.push(`Patient Name : ${patientName}`);
    lines?.push(`Report Period: ${periodStart} – ${periodEnd} (Last ${reportPeriod} days)`);
    lines?.push(`Generated    : ${reportDate}`);
    lines?.push(`Total Entries: ${rawEntries?.length}`);
    lines?.push('');
    if (primaryConcern) {
      lines?.push('PRIMARY CONCERN');
      lines?.push('───────────────');
      lines?.push(`Symptom  : ${fmt(primaryConcern?.name)}`);
      lines?.push(`Location : ${fmt(primaryConcern?.location || 'Not specified')}`);
      lines?.push(`Avg Severity: ${primaryConcern?.avgSeverity}/10`);
      lines?.push(`Frequency: ${primaryConcern?.frequency} occurrence(s)`);
      const trend = primaryTrend?.direction || 'stable';
      lines?.push(`Trend    : ${trend === 'increasing' ? '↑ Worsening' : trend === 'decreasing' ? '↓ Improving' : '→ Stable'}`);
      lines?.push('');
    }
    if (summaryData?.triggers?.length > 0) {
      lines?.push('POSSIBLE TRIGGERS');
      lines?.push('─────────────────');
      summaryData?.triggers?.forEach((t, i) => lines?.push(`${i + 1}. ${t}`));
      lines?.push('');
    }
    if (summaryData?.impacts?.length > 0) {
      lines?.push('IMPACT ON DAILY LIFE');
      lines?.push('────────────────────');
      summaryData?.impacts?.forEach(impact => lines?.push(`${fmt(impact?.category)}: ${impact?.score}/10`));
      lines?.push('');
    }
    const allNoteEntries = rawEntries?.filter(e => e?.notes);
    if (allNoteEntries?.length > 0) {
      lines?.push('PATIENT NOTES');
      lines?.push('─────────────');
      allNoteEntries?.slice(0, 3)?.forEach(entry => {
        const dateStr = new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        lines?.push(`[${dateStr}] ${entry?.notes}`);
      });
      lines?.push('');
    }
    lines?.push('SAFETY DISCLAIMER');
    lines?.push('─────────────────');
    lines?.push('This report is patient-generated and NOT a medical diagnosis.');
    lines?.push('Generated by MyBodyToday™');
    lines?.push('═══════════════════════════════════════');
    return lines?.join('\n');
  };

  const buildReportData = () => {
    const fmt = (str) => str?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase());
    const sortedSymptoms = [...(summaryData?.symptoms || [])]?.sort((a, b) => {
      const scoreA = (a?.avgSeverity || 0) * 0.6 + (a?.frequency || 0) * 0.4;
      const scoreB = (b?.avgSeverity || 0) * 0.6 + (b?.frequency || 0) * 0.4;
      return scoreB - scoreA;
    });
    const primary = sortedSymptoms?.[0] || null;
    const primaryTrendData = primary ? summaryData?.trends?.find(t => t?.symptom === primary?.name) : null;
    const periodStart = new Date(Date.now() - reportPeriod * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const periodEnd = new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const allNoteEntries = rawEntries?.filter(e => e?.notes);
    return {
      patientName: userProfile?.first_name
        ? `${userProfile?.first_name} ${userProfile?.last_name || ''}`?.trim()
        : user?.email || 'Patient',
      reportPeriod: `${periodStart} \u2013 ${periodEnd}`,
      generatedAt: new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' + new Date()?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      totalEntries: rawEntries?.length,
      primarySymptom: primary ? {
        name: fmt(primary?.name),
        location: fmt(primary?.location || 'Not specified'),
        avgSeverity: primary?.avgSeverity,
        trend: primaryTrendData?.direction || 'stable',
        frequency: `${primary?.frequency} occurrence(s) in ${reportPeriod} days`,
        impactLevel: primary?.avgSeverity >= 8 ? 'High' : primary?.avgSeverity >= 5 ? 'Moderate' : 'Low',
      } : null,
      triggers: summaryData?.triggers || [],
      impacts: (summaryData?.impacts || [])?.map(imp => ({ area: fmt(imp?.category), score: imp?.score })),
      timeline: [...rawEntries]?.sort((a, b) => new Date(b?.date) - new Date(a?.date))?.slice(0, 8)?.map(entry => ({
        date: new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        symptoms: entry?.symptomCategories?.map(c => fmt(c))?.join(', ') || 'No symptoms',
        severity: entry?.severityNumeric || 0,
        note: entry?.notes || ''
      })),
      patientNotes: allNoteEntries?.slice(0, 3)?.map(e => e?.notes)?.join(' ') || 'No notes recorded.'
    };
  };

  return (
    <>
      <Helmet>
        <title>Prepare for Doctor Visit - MyBodyToday</title>
        <meta name="description" content="One-page consultation snapshot for your doctor appointment" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24 print:bg-white print:pb-0">
        <div className="print:hidden">
          <BottomTabNavigation />
          <ActionButtonOverlay />
          <UserProfileHeader />
        </div>

        {/* Page Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 print:hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/doctor-summary-export')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back to Doctor Summary"
                >
                  <Icon name="ArrowLeft" className="w-5 h-5 text-gray-600" />
                </button>
                <Icon name="Stethoscope" className="w-5 h-5 text-indigo-600" />
                <h1 className="text-lg font-bold text-gray-900">Prepare for Doctor Visit</h1>
              </div>
              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Icon name="Printer" size={15} />
                <span className="hidden sm:inline">Print / Save PDF</span>
                <span className="sm:hidden">Print</span>
              </button>
            </div>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 print:px-0 print:py-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Loading your health data...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Consultation Snapshot */}
              <div className="lg:col-span-2" id="consultation-snapshot">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <ConsultationHeader
                    patientName={patientName}
                    reportPeriod={reportPeriod}
                    onPeriodChange={setReportPeriod}
                  />

                  <div className="p-5 space-y-5">
                    {/* Primary Symptom */}
                    <PrimarySymptomCard
                      primaryConcern={primaryConcern}
                      primaryTrend={primaryTrend}
                    />

                    {/* All Symptoms Overview */}
                    {summaryData?.symptoms?.length > 1 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">All Symptoms This Period</p>
                        <div className="space-y-2">
                          {summaryData?.symptoms?.slice(0, 5)?.map((symptom, i) => (
                            <div key={i} className="flex items-center justify-between gap-3">
                              <span className="text-sm text-gray-700 truncate flex-1">{formatLabel(symptom?.name)}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getSeverityBadge(symptom?.avgSeverity)}`}>
                                  {symptom?.avgSeverity}/10
                                </span>
                                <span className="text-xs text-gray-400">{symptom?.frequency}×</span>
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${getTrendStyle(summaryData?.trends?.find(t => t?.symptom === symptom?.name)?.direction || 'stable')}`}>
                                  {getTrendIcon(summaryData?.trends?.find(t => t?.symptom === symptom?.name)?.direction || 'stable')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Possible Triggers */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Possible Triggers</p>
                      {summaryData?.triggers?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {summaryData?.triggers?.map((trigger, i) => (
                            <span key={i} className="text-sm bg-white text-amber-800 border border-amber-200 px-2.5 py-1 rounded-lg font-medium">
                              {trigger}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-amber-700 italic">No specific triggers identified</p>
                      )}
                    </div>

                    {/* Impact on Daily Life */}
                    {summaryData?.impacts?.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Impact on Daily Life</p>
                        <div className="grid grid-cols-2 gap-2">
                          {summaryData?.impacts?.slice(0, 4)?.map((impact, i) => (
                            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                              <span className="text-xs text-gray-600 truncate">{formatLabel(impact?.category)}</span>
                              <span className={`text-xs font-bold ml-2 flex-shrink-0 ${getSeverityColor(impact?.score)}`}>
                                {impact?.score}/10
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Short Symptom Timeline */}
                    {timelineEntries?.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Recent Symptom Timeline</p>
                        <div className="space-y-2">
                          {timelineEntries?.map((entry, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <div className="flex-shrink-0 min-w-[56px]">
                                <p className="text-xs font-medium text-gray-600">
                                  {new Date(entry?.date)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              </div>
                              <div className="flex-1 flex items-center gap-2 flex-wrap">
                                {entry?.symptomCategories?.slice(0, 2)?.map((cat, ci) => (
                                  <span key={ci} className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded font-medium">
                                    {formatLabel(cat)}
                                  </span>
                                ))}
                                {entry?.symptomCategories?.length > 2 && (
                                  <span className="text-xs text-gray-400">+{entry?.symptomCategories?.length - 2}</span>
                                )}
                              </div>
                              {entry?.severityNumeric > 0 && (
                                <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded ${getSeverityBadge(entry?.severityNumeric)}`}>
                                  {entry?.severityNumeric}/10
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Patient Notes Summary */}
                    {notesSummary && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Patient Notes Summary</p>
                        <p className="text-sm text-amber-900 leading-relaxed">
                          {notesSummary?.slice(0, 300)}{notesSummary?.length > 300 ? '...' : ''}
                        </p>
                      </div>
                    )}

                    {/* Discussion Questions */}
                    <DiscussionQuestions
                      summaryData={summaryData}
                      primaryConcern={primaryConcern}
                    />

                    {/* Last-Minute Observations */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Last-Minute Observations</p>
                      <textarea
                        value={lastNote}
                        onChange={(e) => setLastNote(e?.target?.value)}
                        placeholder="Add any last-minute concerns or observations before your appointment..."
                        className="w-full text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                        rows={3}
                      />
                    </div>

                    {/* Safety Disclaimer */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-2">
                        <Icon name="ShieldAlert" size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-500 leading-relaxed">
                          This snapshot is a patient-generated symptom summary and is not a medical diagnosis.
                          All data is self-reported. Please consult a qualified healthcare professional for diagnosis and treatment.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 print:hidden">
                <div className="lg:sticky lg:top-24 space-y-4">
                  <ConsultationActions
                    onExportPDF={handleExportPDF}
                    onShare={handleShare}
                    onSave={handleSave}
                    patientName={patientName}
                    reportPeriod={`Last ${reportPeriod} days`}
                    reportText={buildReportText()}
                    reportData={buildReportData()}
                  />

                  {/* Quick Stats */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Quick Stats</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total check-ins</span>
                        <span className="font-semibold text-gray-900">{rawEntries?.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Symptoms tracked</span>
                        <span className="font-semibold text-gray-900">{summaryData?.symptoms?.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Triggers identified</span>
                        <span className="font-semibold text-gray-900">{summaryData?.triggers?.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Period</span>
                        <span className="font-semibold text-gray-900">Last {reportPeriod} days</span>
                      </div>
                    </div>
                  </div>

                  {/* Appointment Tips */}
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Lightbulb" size={15} className="text-indigo-600" />
                      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Appointment Tips</p>
                    </div>
                    <ul className="space-y-1.5">
                      {[
                        'Print or screenshot this page before your appointment',
                        'Bring a list of current medications',
                        'Note any new symptoms since your last visit',
                        'Ask about referrals if symptoms persist'
                      ]?.map((tip, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-indigo-700">
                          <span className="text-indigo-400 mt-0.5">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default PrepareForDoctorVisit;
